import { useEffect, useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import type { PersonSelection } from '@/features/people/types';
import { useAddFromLms } from '@/features/people/use-people';

import {
  addFromLmsErrorMessage,
  grantErrorMessage,
  inviteErrorMessage,
  inviteSuccessToast,
} from '../invite-feedback';
import { useGrantScopeFields } from '../use-grant-scope-fields';
import { useGrantAccess, useInviteProvider } from '../use-provider-grants';
import { GrantScopeFields } from './GrantScopeFields';
import { PersonField } from './PersonField';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Otorgar un acceso temporal: a una persona del QMS, a una del LMS que aún no entró
 *  (se agrega al QMS al otorgar) o a un proveedor externo sin usuario (se le invita).
 *  Accesible (focus trap, ESC). El backend es el guardián real. */
export function GrantAccessModal({ open, onClose }: Props): ReactElement | null {
  const grant = useGrantAccess();
  const invite = useInviteProvider();
  const addFromLms = useAddFromLms();
  const { showToast } = useToast();
  const fields = useGrantScopeFields();
  const { reset } = fields;
  const [selection, setSelection] = useState<PersonSelection | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  useEffect(() => {
    if (!open) {
      return;
    }
    setSelection(null);
    setFirstName('');
    setLastName('');
    reset();
  }, [open, reset]);

  if (!open) {
    return null;
  }

  const busy = grant.isPending || invite.isPending || addFromLms.isPending;
  const isInvite = selection?.kind === 'invite';
  const valid =
    selection !== null &&
    fields.complete &&
    (!isInvite || (firstName.trim() !== '' && lastName.trim() !== ''));

  function grantTo(userId: string, name: string): void {
    grant.mutate(
      { userId, ...fields.payload() },
      {
        onSuccess: () => {
          showToast(`Acceso otorgado a ${name}`);
          onClose();
        },
        onError: (e) => showToast(grantErrorMessage(e), 'error'),
      },
    );
  }

  async function submit(): Promise<void> {
    if (!valid || busy || selection === null) {
      return;
    }
    if (selection.kind === 'qms') {
      grantTo(selection.userId, selection.name);
      return;
    }
    if (selection.kind === 'lms') {
      let userId: string;
      try {
        userId = await addFromLms.mutateAsync({ authSubject: selection.authSubject });
      } catch (e) {
        showToast(addFromLmsErrorMessage(e as Error), 'error');
        return;
      }
      grantTo(userId, selection.name);
      return;
    }
    const email = selection.email;
    invite.mutate(
      { email, firstName: firstName.trim(), lastName: lastName.trim(), ...fields.payload() },
      {
        onSuccess: (r) => {
          showToast(...inviteSuccessToast(r, email));
          onClose();
        },
        // El modal queda abierto con lo escrito: el usuario corrige o reintenta.
        onError: (e) => showToast(inviteErrorMessage(e), 'error'),
      },
    );
  }

  const idleLabel = isInvite ? 'Enviar invitación' : 'Otorgar acceso';
  const busyLabel = isInvite ? 'Enviando…' : 'Otorgando…';
  return (
    <ModalShell
      open
      title="Otorgar acceso temporal"
      wide
      busy={busy}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => void submit()}
            disabled={!valid || busy}
          >
            {busy ? busyLabel : idleLabel}
          </button>
        </>
      }
    >
      <p className="soft" style={{ fontSize: 12.5, margin: '0 0 12px' }}>
        Busca a la persona por nombre o correo. Si es un proveedor sin usuario, escribe su correo
        para invitarlo.
      </p>
      <div className="form-grid">
        <PersonField
          selection={selection}
          onSelect={setSelection}
          firstName={firstName}
          lastName={lastName}
          onFirstName={setFirstName}
          onLastName={setLastName}
        />
        <GrantScopeFields fields={fields} enabled={open} />
      </div>
    </ModalShell>
  );
}
